import { useState, useEffect, useMemo } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import MasonryGallery from "@/components/MasonryGallery";
import { useParams, Link, Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useSEO } from "@/hooks/useCanonical";
import { buildJobSeo } from "@/lib/seo";
import { buildJobJsonLd } from "@/lib/structuredData";
import NotFound from "./NotFound";

const extractFilenameFromUrl = (url: string): string => {
  try {
    const urlPath = new URL(url).pathname;
    const filename = urlPath.split('/').pop() || '';
    // Remove file extension and decode URI components
    return decodeURIComponent(filename.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' '));
  } catch {
    return '';
  }
};

interface Job {
  id: string;
  category_id: string;
  title: string;
  slug: string;
  description: string | null;
  image_layout: string;
}

interface Image {
  id: string;
  url: string;
  caption: string | null;
}

interface Category {
  id: string;
  name: string;
  slug: string;
}

const JobDetail = () => {
  const { category, job: jobSlug } = useParams();
  const [job, setJob] = useState<Job | null>(null);
  const [images, setImages] = useState<Image[]>([]);
  const [relatedJobs, setRelatedJobs] = useState<Job[]>([]);
  const [relatedImages, setRelatedImages] = useState<Record<string, string>>({});
  const [categoryName, setCategoryName] = useState<string | null>(null);
  const [resolvedCategorySlug, setResolvedCategorySlug] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Create JSON-LD dynamically when job and images are loaded
  const jsonLd = useMemo(() => {
    return buildJobJsonLd({
      categorySlug: category,
      categoryName: categoryName || category,
      jobSlug,
      jobTitle: job?.title,
      description: job?.description,
      imageUrls: images.map((img) => img.url),
    });
  }, [job, images, category, jobSlug, categoryName]);

  const seo = buildJobSeo({
    categorySlug: category,
    jobSlug,
    jobTitle: job?.title || jobSlug,
    description: job?.description,
    image: images[0]?.url,
  });

  useSEO({ ...seo, jsonLd });

  useEffect(() => {
    const loadJob = async () => {
      if (!category || !jobSlug) {
        setNotFound(true);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setNotFound(false);
      setJob(null);
      setImages([]);
      setRelatedJobs([]);
      setRelatedImages({});
      setCategoryName(null);
      setResolvedCategorySlug(null);

      const { data: jobData } = await supabase
        .from("jobs")
        .select("*")
        .eq("slug", jobSlug || "")
        .single();

      if (!jobData) {
        setNotFound(true);
        setIsLoading(false);
        return;
      }

      const { data: categoryData } = await supabase
        .from("categories")
        .select("id, name, slug")
        .eq("id", jobData.category_id)
        .single<Category>();

      if (!categoryData) {
        setNotFound(true);
        setIsLoading(false);
        return;
      }

      setResolvedCategorySlug(categoryData.slug);

      if (categoryData.slug !== category) {
        setIsLoading(false);
        return;
      }

      setJob(jobData);
      setCategoryName(categoryData.name);

      const { data: imgsData } = await supabase
        .from("images")
        .select("*")
        .eq("job_id", jobData.id)
        .order("sort_order");

      if (imgsData) setImages(imgsData);

      const { data: relatedJobsData } = await supabase
        .from("jobs")
        .select("*")
        .eq("category_id", jobData.category_id)
        .neq("id", jobData.id)
        .order("sort_order")
        .limit(3);

      if (relatedJobsData) {
        setRelatedJobs(relatedJobsData);

        const imageMap: Record<string, string> = {};
        for (const relJob of relatedJobsData) {
          const { data: firstImg } = await supabase
            .from("images")
            .select("url")
            .eq("job_id", relJob.id)
            .order("sort_order")
            .limit(1)
            .single();

          if (firstImg) {
            imageMap[relJob.id] = firstImg.url;
          }
        }
        setRelatedImages(imageMap);
      }

      setIsLoading(false);
    };

    loadJob();
  }, [category, jobSlug]);

  if (isLoading) return null;

  if (resolvedCategorySlug && resolvedCategorySlug !== category) {
    return <Navigate to={`/work/${resolvedCategorySlug}/${jobSlug}`} replace />;
  }

  if (notFound || !job) {
    return <NotFound />;
  }

  // Prepare images for MasonryGallery
  const galleryImages = images.map((img) => {
    const filename = extractFilenameFromUrl(img.url);
    return {
      src: img.url,
      alt: img.caption || filename || job?.title || "Image",
      title: filename,
    };
  });

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navigation />
      <main className="pt-32 pb-16 flex-1">
        {/* Description - always constrained width */}
        {job.description && (
          <div className="container mx-auto px-4 sm:px-8 max-w-6xl">
            <p className="text-muted-foreground leading-relaxed mb-12">{job.description}</p>
          </div>
        )}

        {/* Single column layout */}
        {job.image_layout === 'single' && (
          <div className="container mx-auto px-4 sm:px-8 max-w-6xl">
            <div className="space-y-8 mb-24">
              {images.map((img) => {
                const filename = extractFilenameFromUrl(img.url);
                return (
                  <img 
                    key={img.id} 
                    src={img.url} 
                    alt={img.caption || filename || job.title} 
                    title={filename}
                    className="w-full rounded-lg" 
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Two column masonry layout - same as home page */}
        {job.image_layout === 'grid-2' && (
          <div className="mb-24 max-w-6xl mx-auto">
            <MasonryGallery images={galleryImages} columnCount={2} />
          </div>
        )}

        {/* Three column masonry layout - same as home page */}
        {job.image_layout === 'grid-3' && (
          <div className="mb-24 max-w-6xl mx-auto">
            <MasonryGallery images={galleryImages} columnCount={3} />
          </div>
        )}

        {/* Mixed layout - alternating 3 and 2 column rows */}
        {job.image_layout === 'mixed' && (
          <div className="mb-24 max-w-6xl mx-auto">
            <MasonryGallery images={galleryImages} columnCount="mixed" />
          </div>
        )}

        {/* Related Jobs Section */}
        <div className="container mx-auto px-4 sm:px-8">
          {relatedJobs.length > 0 && (
            <div className="border-t border-border/40 pt-16">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedJobs.map((relJob) => (
                  <Link key={relJob.id} to={`/work/${category}/${relJob.slug}`} className="group">
                    <div className="aspect-square overflow-hidden mb-3 rounded-lg">
                      {relatedImages[relJob.id] && (
                        <img
                          src={relatedImages[relJob.id]}
                          alt={relJob.title}
                          className="w-full h-full object-cover transition-transform duration-500"
                        />
                      )}
                    </div>
                    <p className="relative text-sm text-foreground tracking-wide transition-colors text-center font-sans inline-block">
                      {relJob.title}
                      <span className="absolute -bottom-1 left-0 right-0 h-px bg-accent scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default JobDetail;
